import AOVoxelGrid from './AOVoxelGrid.js';
import AOTextureGenerator from './AOTextureGenerator.js';
import AOSpatialIndex from './AOSpatialIndex.js';
import Cuboid from '../rendering/Cuboid.js';

/**
 * Ambient Occlusion system for CSS 3D engine.
 * Uses voxel grid ray marching for fast, accurate AO.
 * Generates per-texel lightmap textures for cuboid faces.
 * Static class following the SunVector pattern.
 *
 * IMPORTANT: Uses per-window caching to support same-origin iframes.
 * Each window/iframe context gets its own isolated cache to prevent
 * cross-contamination when the app is embedded inside itself.
 */
export default class AmbientOcclusion {
  // --- Configuration (shared across all contexts) ---
  static enabled = false;       // AO disabled by default
  static intensity = 0.5;       // 0-2, how dark shadows get (higher = darker)
  static radius = 1.5;          // World units for ray march distance
  static numRays = 8;           // Rays per sample (8-32 recommended)
  static texelsPerUnit = 4;     // AO texture resolution (2-8 recommended)

  // --- Per-window cache storage ---
  // Uses WeakMap to allow garbage collection when windows close
  static _windowCaches = new WeakMap();

  // --- Transparency support ---
  static considerTransparency = true;  // Whether AO considers face transparency
  static transparencyAOExponent = 6;   // Power curve for semi-transparent AO (higher = less shadow)

  // --- Player position for distance-based priority ---
  static _playerPos = null;
  static _lastPriorityPos = null;

  // --- Incremental update config (shared) ---
  static _updateBudgetMs = 4;         // Max ms per frame for AO updates
  static _burstThreshold = 50;        // Switch to burst budget when queue > this
  static _burstBudgetMs = 10;         // Budget during burst mode (ms)
  static _normalBudgetMs = 4;         // Budget during normal operation (ms)

  /**
   * Get or create the cache object for the current window context.
   * Each window (including same-origin iframes) gets isolated caches.
   * @returns {Object} Cache object with aoDataCache, textureCache, dirtyFaces, etc.
   */
  static _getWindowCache() {
    const win = typeof window !== 'undefined' ? window : globalThis;

    if (!this._windowCaches.has(win)) {
      this._windowCaches.set(win, {
        aoDataCache: new Map(),      // "cuboidKey|face" -> Float32Array
        textureCache: new Map(),     // "cuboidKey|face" -> dataURL
        dirtyFaces: new Set(),       // Set of "cuboidKey|face" needing recalc
        voxelGrid: null,             // AOVoxelGrid instance
        spatialIndex: null,          // AOSpatialIndex for fast invalidation queries
        world: null,                 // Reference to world
        lastConfigKey: null,         // Config change detection
        pendingUpdates: [],          // Queue of {cuboid, face, cacheKey, priority}
        pendingSet: new Set(),       // Track what's already queued (cacheKey)
        pendingIndex: 0,             // Current position in pendingUpdates array
        pendingSortDirty: false,     // Whether queue needs re-sorting
        levelSettling: false         // True while initial level AO is still filling in
      });
    }

    return this._windowCaches.get(win);
  }

  // --- Convenience accessors for the current window's cache ---
  static get _aoDataCache() { return this._getWindowCache().aoDataCache; }
  static get _textureCache() { return this._getWindowCache().textureCache; }
  static get _dirtyFaces() { return this._getWindowCache().dirtyFaces; }
  static get _voxelGrid() { return this._getWindowCache().voxelGrid; }
  static set _voxelGrid(v) { this._getWindowCache().voxelGrid = v; }
  static get _spatialIndex() { return this._getWindowCache().spatialIndex; }
  static set _spatialIndex(v) { this._getWindowCache().spatialIndex = v; }
  static get _world() { return this._getWindowCache().world; }
  static set _world(v) { this._getWindowCache().world = v; }
  static get _lastConfigKey() { return this._getWindowCache().lastConfigKey; }
  static set _lastConfigKey(v) { this._getWindowCache().lastConfigKey = v; }
  static get _pendingUpdates() { return this._getWindowCache().pendingUpdates; }
  static set _pendingUpdates(v) { this._getWindowCache().pendingUpdates = v; }
  static get _pendingSet() { return this._getWindowCache().pendingSet; }
  static get _pendingIndex() { return this._getWindowCache().pendingIndex; }
  static set _pendingIndex(v) { this._getWindowCache().pendingIndex = v; }
  static get _pendingSortDirty() { return this._getWindowCache().pendingSortDirty; }
  static set _pendingSortDirty(v) { this._getWindowCache().pendingSortDirty = v; }
  static get _levelSettling() { return this._getWindowCache().levelSettling; }
  static set _levelSettling(v) { this._getWindowCache().levelSettling = v; }

  /**
   * Initialize the AO system with a world reference.
   * Builds the voxel grid and spatial index from existing cuboids.
   * @param {World} world - The game world
   */
  static initialize(world) {
    this._world = world;
    this._voxelGrid = new AOVoxelGrid(1); // 1 unit cell size (matches cube grid)
    this._voxelGrid.rebuild(world.cuboids);

    // Build spatial index for fast invalidation queries (cell size = radius + margin)
    this._spatialIndex = new AOSpatialIndex(Math.max(2, Math.ceil(this.radius + 1)));
    this._spatialIndex.rebuild(world.cuboids);

    this.invalidateAll();
    this._lastConfigKey = this._getConfigKey(); // Prevent _checkConfigChange from re-clearing
    this._levelSettling = true; // Burst mode until initial AO fill completes

    // Queue all cuboid faces for non-blocking calculation
    // This ensures AO fills in even when cuboids are static (update() bails out early)
    this._queueAllWorldFaces(this._playerPos);
  }

  /**
   * Queue all world cuboid faces for AO calculation.
   * Used during initialize() and when settings change in non-dungeon mode.
   * @param {Object} [playerPos] - Optional player position for distance-based priority
   */
  static _queueAllWorldFaces(playerPos = null) {
    if (!this._world) return;
    const faces = ['top', 'front', 'back', 'left', 'right'];
    for (const [key, cuboid] of this._world.cuboids) {
      this.invalidateCuboid(key);
      let priority = 0;
      if (playerPos) {
        // Higher priority for cuboids closer to the player
        const dx = (cuboid.x + cuboid.width / 2) - playerPos.x;
        const dy = (cuboid.y + cuboid.height / 2) - playerPos.y;
        const dz = (cuboid.z + cuboid.depth / 2) - playerPos.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        priority = Math.max(0, 100 - dist);
      }
      for (const face of faces) {
        this.queueUpdate(cuboid, face, priority);
      }
    }
  }

  /**
   * Get configuration key for change detection.
   * @returns {string} Config key
   */
  static _getConfigKey() {
    return `${this.enabled}|${this.intensity}|${this.radius}|${this.numRays}|${this.texelsPerUnit}|${this.considerTransparency}`;
  }

  /**
   * Check if config has changed and invalidate if needed.
   */
  static _checkConfigChange() {
    const configKey = this._getConfigKey();
    if (this._lastConfigKey !== configKey) {
      this.invalidateAll();
      this._lastConfigKey = configKey;
      this._levelSettling = true;

      if (this._dungeonPrecalcMode && this._dungeonDefs) {
        // Dungeon mode: re-trigger precalculation with new config
        this.precalculateForDefs(this._dungeonDefs);
      } else if (this._world) {
        // World mode: re-queue all faces with fresh settings
        this._queueAllWorldFaces(this._playerPos);
      }
    }
  }

  /**
   * Get the AO texture for a cuboid face.
   * Returns cached texture or calculates if needed.
   * For fragments, reuses parent's AO data instead of recalculating.
   * Supports non-blocking mode where stale textures are returned while updates are queued.
   * @param {Cuboid} cuboid - The cuboid
   * @param {string} face - Face name ('top', 'front', etc.)
   * @param {boolean} nonBlocking - If true, return stale cache and queue update (default: false for backwards compatibility)
   * @returns {string|null} Data URL of texture, or null if AO disabled
   */
  static getTextureForFace(cuboid, face, nonBlocking = false) {
    if (!this.enabled || !this._voxelGrid) return null;

    this._checkConfigChange();

    // Swap front/back faces for correct AO mapping
    let samplingFace = face;
    if (face === 'front') samplingFace = 'back';
    else if (face === 'back') samplingFace = 'front';

    const cacheKey = `${cuboid.getKey()}|${samplingFace}`;

    // For fragments, also check if parent is dirty
    let parentDirty = false;
    let parentCuboid = null;
    if (cuboid.isFragment && cuboid.parentKey && this._world) {
      parentCuboid = this._world.cuboids.get(cuboid.parentKey);
      if (parentCuboid) {
        const parentCacheKey = `${parentCuboid.getKey()}|${samplingFace}`;
        parentDirty = this._dirtyFaces.has(parentCacheKey);
      }
    }

    // Check cache first (also invalidate fragment if parent is dirty)
    const isDirty = this._dirtyFaces.has(cacheKey) || parentDirty;
    const hasCachedTexture = this._textureCache.has(cacheKey);

    if (!isDirty && hasCachedTexture) {
      return this._textureCache.get(cacheKey);
    }

    // Non-blocking mode: return stale texture and queue update
    if (nonBlocking && hasCachedTexture) {
      // Queue update for later processing
      this.queueUpdate(cuboid, samplingFace, 0);
      return this._textureCache.get(cacheKey);
    }

    // For fragments, extract from parent's AO data instead of recalculating
    if (parentCuboid) {
      // Non-blocking: if parent AO isn't cached yet, queue parent and return null
      if (nonBlocking) {
        const parentCacheKey = `${parentCuboid.getKey()}|${samplingFace}`;
        if (this._dirtyFaces.has(parentCacheKey) || !this._aoDataCache.has(parentCacheKey)) {
          this.queueUpdate(parentCuboid, samplingFace, 1);
          return null;
        }
      }
      const texture = this._getFragmentTexture(cuboid, parentCuboid, samplingFace, cacheKey);
      if (texture) {
        this._dirtyFaces.delete(cacheKey);
        return texture;
      }
    } else if (cuboid.isFragment && cuboid.parentKey && cuboid.textureOrigin) {
      // Parent cuboid was removed (subdivided) but we may still have cached AO data
      // Use textureOrigin to extract the correct portion
      const texture = this._getFragmentTextureFromCache(cuboid, cuboid.parentKey, samplingFace, cacheKey);
      if (texture) {
        this._dirtyFaces.delete(cacheKey);
        return texture;
      }

      // Non-blocking mode: queue the parent calculation instead of blocking
      if (nonBlocking) {
        this.queueUpdate(cuboid, samplingFace, 1); // Higher priority for fragments without cache
        return null;
      }

      // Parent data not in cache - calculate AO for "virtual parent" using textureOrigin
      // This ensures all fragments from the same parent get consistent AO
      const parentCacheKey = `${cuboid.parentKey}|${samplingFace}`;
      this._calculateFaceAOForOrigin(cuboid.textureOrigin, samplingFace, parentCacheKey);

      // Now extract fragment's portion from the newly calculated parent data
      const textureFromNewParent = this._getFragmentTextureFromCache(cuboid, cuboid.parentKey, samplingFace, cacheKey);
      if (textureFromNewParent) {
        this._dirtyFaces.delete(cacheKey);
        return textureFromNewParent;
      }
    }

    // Non-blocking mode: queue update if no cache exists
    if (nonBlocking) {
      this.queueUpdate(cuboid, samplingFace, 2); // Highest priority for faces with no cache
      return null;
    }

    // Regular cuboid (not a fragment) - calculate full AO (blocking)
    this._calculateFaceAO(cuboid, samplingFace, cacheKey);
    this._dirtyFaces.delete(cacheKey);

    return this._textureCache.get(cacheKey);
  }

  /**
   * Get AO texture for a fragment by extracting from parent's AO data.
   * Much faster than recalculating since no ray marching needed.
   * @param {Cuboid} fragment - The fragment cuboid
   * @param {Cuboid} parent - The parent cuboid
   * @param {string} face - Face name
   * @param {string} cacheKey - Cache key for the fragment
   * @returns {string|null} Data URL of texture
   */
  static _getFragmentTexture(fragment, parent, face, cacheKey) {
    // Ensure parent's AO is calculated (recalculate if dirty)
    const parentCacheKey = `${parent.getKey()}|${face}`;
    if (this._dirtyFaces.has(parentCacheKey) || !this._aoDataCache.has(parentCacheKey)) {
      this._calculateFaceAO(parent, face, parentCacheKey);
      this._dirtyFaces.delete(parentCacheKey);
    }

    const parentAoData = this._aoDataCache.get(parentCacheKey);
    if (!parentAoData) return null;

    // Get parent's face dimensions and resolution
    const parentDims = this._getFaceDimensions(parent, face);
    const parentResX = Math.max(2, Math.min(64, Math.ceil(parentDims.width * this.texelsPerUnit)));
    const parentResY = Math.max(2, Math.min(64, Math.ceil(parentDims.height * this.texelsPerUnit)));

    // Calculate fragment's UV bounds within parent (0-1 range)
    const uvBounds = this._getFragmentUVBounds(fragment, parent, face);

    // Calculate fragment's texture resolution
    const fragDims = this._getFaceDimensions(fragment, face);
    const fragResX = Math.max(2, Math.min(64, Math.ceil(fragDims.width * this.texelsPerUnit)));
    const fragResY = Math.max(2, Math.min(64, Math.ceil(fragDims.height * this.texelsPerUnit)));

    // Extract portion of parent's AO data with bilinear sampling
    const fragAoData = new Float32Array(fragResX * fragResY);

    for (let fy = 0; fy < fragResY; fy++) {
      for (let fx = 0; fx < fragResX; fx++) {
        // Map fragment texel to parent UV coordinates
        const u = uvBounds.uMin + ((fx + 0.5) / fragResX) * (uvBounds.uMax - uvBounds.uMin);
        const v = uvBounds.vMin + ((fy + 0.5) / fragResY) * (uvBounds.vMax - uvBounds.vMin);

        // Convert to parent texel coordinates
        const px = u * parentResX - 0.5;
        const py = v * parentResY - 0.5;

        // Bilinear sample from parent
        const ao = this._bilinearSample(parentAoData, parentResX, parentResY, px, py);
        fragAoData[fy * fragResX + fx] = ao;
      }
    }

    // Cache and generate texture
    this._aoDataCache.set(cacheKey, fragAoData);
    const texture = AOTextureGenerator.generateTextureUpscaled(fragAoData, fragResX, fragResY, 2);
    this._textureCache.set(cacheKey, texture);

    return texture;
  }

  /**
   * Get AO texture for a fragment using cached parent data and textureOrigin.
   * Used when parent cuboid is no longer in the world (was subdivided).
   * @param {Cuboid} fragment - The fragment cuboid
   * @param {string} parentKey - The parent's key
   * @param {string} face - Face name
   * @param {string} cacheKey - Cache key for the fragment
   * @returns {string|null} Data URL of texture
   */
  static _getFragmentTextureFromCache(fragment, parentKey, face, cacheKey) {
    // Check if parent's AO data is still in cache AND not dirty
    const parentCacheKey = `${parentKey}|${face}`;

    // If parent is marked dirty, we can't use the cached data
    if (this._dirtyFaces.has(parentCacheKey)) {
      // Clear the stale parent data since we can't recalculate it (parent removed)
      this._aoDataCache.delete(parentCacheKey);
      this._textureCache.delete(parentCacheKey);
      this._dirtyFaces.delete(parentCacheKey);
      return null;
    }

    const parentAoData = this._aoDataCache.get(parentCacheKey);
    if (!parentAoData) return null;

    // Use textureOrigin for parent dimensions (stored when fragment was created)
    const origin = fragment.textureOrigin;
    const parentDims = this._getFaceDimensionsFromOrigin(origin, face);
    const parentResX = Math.max(2, Math.min(64, Math.ceil(parentDims.width * this.texelsPerUnit)));
    const parentResY = Math.max(2, Math.min(64, Math.ceil(parentDims.height * this.texelsPerUnit)));

    // Calculate fragment's UV bounds using textureOrigin
    const uvBounds = this._getFragmentUVBoundsFromOrigin(fragment, origin, face);

    // Calculate fragment's texture resolution
    const fragDims = this._getFaceDimensions(fragment, face);
    const fragResX = Math.max(2, Math.min(64, Math.ceil(fragDims.width * this.texelsPerUnit)));
    const fragResY = Math.max(2, Math.min(64, Math.ceil(fragDims.height * this.texelsPerUnit)));

    // Extract portion of parent's AO data with bilinear sampling
    const fragAoData = new Float32Array(fragResX * fragResY);

    for (let fy = 0; fy < fragResY; fy++) {
      for (let fx = 0; fx < fragResX; fx++) {
        const u = uvBounds.uMin + ((fx + 0.5) / fragResX) * (uvBounds.uMax - uvBounds.uMin);
        const v = uvBounds.vMin + ((fy + 0.5) / fragResY) * (uvBounds.vMax - uvBounds.vMin);
        const px = u * parentResX - 0.5;
        const py = v * parentResY - 0.5;
        const ao = this._bilinearSample(parentAoData, parentResX, parentResY, px, py);
        fragAoData[fy * fragResX + fx] = ao;
      }
    }

    // Cache and generate texture
    this._aoDataCache.set(cacheKey, fragAoData);
    const texture = AOTextureGenerator.generateTextureUpscaled(fragAoData, fragResX, fragResY, 2);
    this._textureCache.set(cacheKey, texture);

    return texture;
  }

  /**
   * Get face dimensions from textureOrigin (for fragments without parent object).
   * @param {Object} origin - The textureOrigin object
   * @param {string} face - Face name
   * @returns {Object} {width, height}
   */
  static _getFaceDimensionsFromOrigin(origin, face) {
    switch (face) {
      case 'top':
      case 'bottom':
        return { width: origin.width, height: origin.depth };
      case 'front':
      case 'back':
        return { width: origin.width, height: origin.height };
      case 'left':
      case 'right':
        return { width: origin.depth, height: origin.height };
      default:
        return { width: 1, height: 1 };
    }
  }

  /**
   * Calculate fragment's UV bounds using textureOrigin instead of parent object.
   * @param {Cuboid} fragment - The fragment
   * @param {Object} origin - The textureOrigin object
   * @param {string} face - Face name
   * @returns {Object} {uMin, uMax, vMin, vMax} in 0-1 range
   */
  static _getFragmentUVBoundsFromOrigin(fragment, origin, face) {
    const relX = fragment.x - origin.x;
    const relY = fragment.y - origin.y;
    const relZ = fragment.z - origin.z;

    switch (face) {
      case 'top': {
        const uMin = relX / origin.width;
        const uMax = (relX + fragment.width) / origin.width;
        const vMin = (origin.depth - relZ - fragment.depth) / origin.depth;
        const vMax = (origin.depth - relZ) / origin.depth;
        return { uMin, uMax, vMin, vMax };
      }
      case 'bottom': {
        const uMin = relX / origin.width;
        const uMax = (relX + fragment.width) / origin.width;
        const vMin = relZ / origin.depth;
        const vMax = (relZ + fragment.depth) / origin.depth;
        return { uMin, uMax, vMin, vMax };
      }
      case 'front':
      case 'back': {
        let uMin, uMax;
        if (face === 'front') {
          uMin = (origin.width - relX - fragment.width) / origin.width;
          uMax = (origin.width - relX) / origin.width;
        } else {
          uMin = relX / origin.width;
          uMax = (relX + fragment.width) / origin.width;
        }
        const vMin = (origin.height - relY - fragment.height) / origin.height;
        const vMax = (origin.height - relY) / origin.height;
        return { uMin, uMax, vMin, vMax };
      }
      case 'left':
      case 'right': {
        let uMin, uMax;
        if (face === 'left') {
          uMin = (origin.depth - relZ - fragment.depth) / origin.depth;
          uMax = (origin.depth - relZ) / origin.depth;
        } else {
          uMin = relZ / origin.depth;
          uMax = (relZ + fragment.depth) / origin.depth;
        }
        const vMin = (origin.height - relY - fragment.height) / origin.height;
        const vMax = (origin.height - relY) / origin.height;
        return { uMin, uMax, vMin, vMax };
      }
      default:
        return { uMin: 0, uMax: 1, vMin: 0, vMax: 1 };
    }
  }

  /**
   * Get face dimensions (width and height in world units).
   * @param {Cuboid} cuboid - The cuboid
   * @param {string} face - Face name
   * @returns {Object} {width, height}
   */
  static _getFaceDimensions(cuboid, face) {
    switch (face) {
      case 'top':
      case 'bottom':
        return { width: cuboid.width, height: cuboid.depth };
      case 'front':
      case 'back':
        return { width: cuboid.width, height: cuboid.height };
      case 'left':
      case 'right':
        return { width: cuboid.depth, height: cuboid.height };
      default:
        return { width: 1, height: 1 };
    }
  }

  /**
   * Calculate fragment's UV bounds within parent's face texture.
   * @param {Cuboid} fragment - The fragment
   * @param {Cuboid} parent - The parent cuboid
   * @param {string} face - Face name
   * @returns {Object} {uMin, uMax, vMin, vMax} in 0-1 range
   */
  static _getFragmentUVBounds(fragment, parent, face) {
    // Calculate relative position of fragment within parent
    const relX = fragment.x - parent.x;
    const relY = fragment.y - parent.y;
    const relZ = fragment.z - parent.z;

    switch (face) {
      case 'top': {
        // Top: U = X axis (left to right), V = Z axis (front to back)
        const uMin = relX / parent.width;
        const uMax = (relX + fragment.width) / parent.width;
        // V axis goes from front (V=0) to back (V=1)
        const vMin = (parent.depth - relZ - fragment.depth) / parent.depth;
        const vMax = (parent.depth - relZ) / parent.depth;
        return { uMin, uMax, vMin, vMax };
      }
      case 'bottom': {
        // Bottom: U = X axis (left to right), V = Z axis (back to front)
        const uMin = relX / parent.width;
        const uMax = (relX + fragment.width) / parent.width;
        // V axis goes from back (V=0) to front (V=1)
        const vMin = relZ / parent.depth;
        const vMax = (relZ + fragment.depth) / parent.depth;
        return { uMin, uMax, vMin, vMax };
      }
      case 'front':
      case 'back': {
        // Front/back: U = X axis (may be inverted), V = Y axis
        let uMin, uMax;
        if (face === 'front') {
          // Front face: origin at right, U goes left
          uMin = (parent.width - relX - fragment.width) / parent.width;
          uMax = (parent.width - relX) / parent.width;
        } else {
          // Back face: origin at left, U goes right
          uMin = relX / parent.width;
          uMax = (relX + fragment.width) / parent.width;
        }
        // V axis goes from top to bottom (Y+ to Y-)
        const vMin = (parent.height - relY - fragment.height) / parent.height;
        const vMax = (parent.height - relY) / parent.height;
        return { uMin, uMax, vMin, vMax };
      }
      case 'left':
      case 'right': {
        // Left/right: U = Z axis (may be inverted), V = Y axis
        let uMin, uMax;
        if (face === 'left') {
          // Left face: origin at front, U goes back
          uMin = (parent.depth - relZ - fragment.depth) / parent.depth;
          uMax = (parent.depth - relZ) / parent.depth;
        } else {
          // Right face: origin at back, U goes front
          uMin = relZ / parent.depth;
          uMax = (relZ + fragment.depth) / parent.depth;
        }
        // V axis goes from top to bottom
        const vMin = (parent.height - relY - fragment.height) / parent.height;
        const vMax = (parent.height - relY) / parent.height;
        return { uMin, uMax, vMin, vMax };
      }
      default:
        return { uMin: 0, uMax: 1, vMin: 0, vMax: 1 };
    }
  }

  /**
   * Bilinear sample from AO data array.
   * @param {Float32Array} data - AO data
   * @param {number} width - Data width
   * @param {number} height - Data height
   * @param {number} x - Sample X (can be fractional)
   * @param {number} y - Sample Y (can be fractional)
   * @returns {number} Interpolated AO value
   */
  static _bilinearSample(data, width, height, x, y) {
    const x0 = Math.max(0, Math.min(width - 1, Math.floor(x)));
    const y0 = Math.max(0, Math.min(height - 1, Math.floor(y)));
    const x1 = Math.min(width - 1, x0 + 1);
    const y1 = Math.min(height - 1, y0 + 1);

    const fx = x - x0;
    const fy = y - y0;

    const v00 = data[y0 * width + x0];
    const v10 = data[y0 * width + x1];
    const v01 = data[y1 * width + x0];
    const v11 = data[y1 * width + x1];

    // Bilinear interpolation
    const v0 = v00 * (1 - fx) + v10 * fx;
    const v1 = v01 * (1 - fx) + v11 * fx;
    return v0 * (1 - fy) + v1 * fy;
  }

  /**
   * Get the opacity of a cuboid's face (0 = fully transparent, 1 = fully opaque).
   * Considers direct material opacity, MaterialGroup opacity, and special effects.
   * @param {Cuboid} cuboid - The cuboid
   * @param {string} faceName - The face name
   * @returns {number} Opacity value 0-1
   */
  static getFaceOpacity(cuboid, faceName) {
    // Start with full opacity
    let opacity = 1.0;

    // Plain def objects (from dungeon precalc) don't have materials or getKey()
    // They are always fully opaque geometry
    if (typeof cuboid.getKey !== 'function') return 1.0;

    // Check direct material on the face
    const material = cuboid.materials?.[faceName];
    if (material) {
      opacity = Math.min(opacity, material.opacity ?? 1.0);
      // Glass effect is treated as semi-transparent
      if (material.effectType === 'glass') {
        opacity = Math.min(opacity, 0.3); // Glass is quite transparent
      }
    }

    // Check MaterialGroup opacity
    if (this._world && this._world.materialGroupManager) {
      const group = this._world.materialGroupManager.getGroupForFace(cuboid.getKey(), faceName);
      if (group && group.material) {
        // Check group's material opacity
        if (typeof group.material.opacity === 'number') {
          opacity = Math.min(opacity, group.material.opacity);
        }
        // Check group's colorOpacity (only applies to color materials)
        if (group.material.type === 'color' && typeof group.colorOpacity === 'number') {
          opacity = Math.min(opacity, group.colorOpacity);
        }
      }
    }

    // Apply power curve so semi-transparent materials contribute very little AO
    // e.g., opacity 0.3 -> 0.027 (nearly no shadow), opacity 1.0 -> 1.0 (unchanged)
    if (opacity < 1.0 && this.transparencyAOExponent > 1) {
      opacity = Math.pow(opacity, this.transparencyAOExponent);
    }

    return opacity;
  }

  /**
   * Create an opacity callback for use with transparency-aware AO sampling.
   * @returns {Function} Callback (cuboid, faceName) => opacity (0-1)
   */
  static _createOpacityCallback() {
    return (cuboid, faceName) => this.getFaceOpacity(cuboid, faceName);
  }

  /**
   * Calculate AO for a specific face using voxel grid ray marching.
   * Resolution scales with face size for consistent texel density.
   * Optimized: pre-computes rays and inlines world position calculation.
   * @param {Cuboid} cuboid - The cuboid
   * @param {string} face - Face name
   * @param {string} cacheKey - Cache key for storing results
   */
  static _calculateFaceAO(cuboid, face, cacheKey) {
    // Calculate face dimensions based on face type
    const { width, height, depth } = cuboid;
    let faceWidth, faceHeight;
    switch (face) {
      case 'top':
      case 'bottom':
        faceWidth = width;
        faceHeight = depth;
        break;
      case 'front':
      case 'back':
        faceWidth = width;
        faceHeight = height;
        break;
      case 'left':
      case 'right':
        faceWidth = depth;
        faceHeight = height;
        break;
      default:
        faceWidth = 1;
        faceHeight = 1;
    }

    // Calculate resolution based on face size and texelsPerUnit setting
    const MIN_RESOLUTION = 2;
    const MAX_RESOLUTION = 64;
    const resX = Math.max(MIN_RESOLUTION, Math.min(MAX_RESOLUTION, Math.ceil(faceWidth * this.texelsPerUnit)));
    const resY = Math.max(MIN_RESOLUTION, Math.min(MAX_RESOLUTION, Math.ceil(faceHeight * this.texelsPerUnit)));

    const aoData = new Float32Array(resX * resY);

    // Get face bounds in world coordinates (computed ONCE)
    const faceBounds = this._getFaceWorldBounds(cuboid, face);
    const normal = Cuboid.FACE_NORMALS[face];

    // Pre-compute rays ONCE for this face (major optimization)
    const rays = this._voxelGrid.getHemisphereRays(normal, this.numRays);

    // Pre-compute constants for world position calculation
    const offset = 0.05;
    const originX = faceBounds.origin.x + normal.x * offset;
    const originY = faceBounds.origin.y + normal.y * offset;
    const originZ = faceBounds.origin.z + normal.z * offset;

    // Pre-compute UV scale factors
    const uScaleX = faceBounds.uAxis.x;
    const uScaleY = faceBounds.uAxis.y;
    const uScaleZ = faceBounds.uAxis.z;
    const vScaleX = faceBounds.vAxis.x;
    const vScaleY = faceBounds.vAxis.y;
    const vScaleZ = faceBounds.vAxis.z;

    // Cache frequently used values
    const radius = this.radius;
    const intensity = this.intensity;
    const voxelGrid = this._voxelGrid;

    // Create opacity callback for transparency-aware AO
    const opacityCallback = this.considerTransparency ? this._createOpacityCallback() : null;

    // For each texel, calculate AO using voxel grid ray marching
    for (let ty = 0; ty < resY; ty++) {
      const v = (ty + 0.5) / resY;
      // Pre-compute V contribution for this row
      const vContribX = vScaleX * v;
      const vContribY = vScaleY * v;
      const vContribZ = vScaleZ * v;

      for (let tx = 0; tx < resX; tx++) {
        const u = (tx + 0.5) / resX;

        // Inline world position calculation (avoids function call overhead)
        const worldPos = {
          x: originX + uScaleX * u + vContribX,
          y: originY + uScaleY * u + vContribY,
          z: originZ + uScaleZ * u + vContribZ
        };

        // Sample AO - use transparency-aware version if enabled
        let ao;
        if (opacityCallback) {
          ao = voxelGrid.sampleAOWithTransparency(worldPos, rays, radius, intensity, cuboid, opacityCallback);
        } else {
          ao = voxelGrid.sampleAOWithRays(worldPos, rays, radius, intensity, cuboid);
        }
        aoData[ty * resX + tx] = ao;
      }
    }

    // Cache the raw data
    this._aoDataCache.set(cacheKey, aoData);

    // Generate and cache the texture (upscale 2x for smoother gradients)
    const texture = AOTextureGenerator.generateTextureUpscaled(
      aoData, resX, resY, 2
    );
    this._textureCache.set(cacheKey, texture);
  }

  /**
   * Calculate AO for a "virtual parent" using textureOrigin bounds.
   * Used when a fragment needs AO but its parent is no longer in the world.
   * Optimized: pre-computes rays and inlines world position calculation.
   * @param {Object} origin - The textureOrigin object with x, y, z, width, height, depth
   * @param {string} face - Face name
   * @param {string} cacheKey - Cache key for the parent
   */
  static _calculateFaceAOForOrigin(origin, face, cacheKey) {
    // Calculate face dimensions based on face type
    const { width, height, depth } = origin;
    let faceWidth, faceHeight;
    switch (face) {
      case 'top':
      case 'bottom':
        faceWidth = width;
        faceHeight = depth;
        break;
      case 'front':
      case 'back':
        faceWidth = width;
        faceHeight = height;
        break;
      case 'left':
      case 'right':
        faceWidth = depth;
        faceHeight = height;
        break;
      default:
        faceWidth = 1;
        faceHeight = 1;
    }

    // Calculate resolution based on face size and texelsPerUnit setting
    const MIN_RESOLUTION = 2;
    const MAX_RESOLUTION = 64;
    const resX = Math.max(MIN_RESOLUTION, Math.min(MAX_RESOLUTION, Math.ceil(faceWidth * this.texelsPerUnit)));
    const resY = Math.max(MIN_RESOLUTION, Math.min(MAX_RESOLUTION, Math.ceil(faceHeight * this.texelsPerUnit)));

    const aoData = new Float32Array(resX * resY);

    // Get face bounds using origin (create a pseudo-cuboid object)
    const pseudoCuboid = {
      x: origin.x,
      y: origin.y,
      z: origin.z,
      width: origin.width,
      height: origin.height,
      depth: origin.depth
    };
    const faceBounds = this._getFaceWorldBounds(pseudoCuboid, face);
    const normal = Cuboid.FACE_NORMALS[face];

    // Pre-compute rays ONCE for this face (major optimization)
    const rays = this._voxelGrid.getHemisphereRays(normal, this.numRays);

    // Pre-compute constants for world position calculation
    const offset = 0.05;
    const originX = faceBounds.origin.x + normal.x * offset;
    const originY = faceBounds.origin.y + normal.y * offset;
    const originZ = faceBounds.origin.z + normal.z * offset;

    // Pre-compute UV scale factors
    const uScaleX = faceBounds.uAxis.x;
    const uScaleY = faceBounds.uAxis.y;
    const uScaleZ = faceBounds.uAxis.z;
    const vScaleX = faceBounds.vAxis.x;
    const vScaleY = faceBounds.vAxis.y;
    const vScaleZ = faceBounds.vAxis.z;

    // Cache frequently used values
    const radius = this.radius;
    const intensity = this.intensity;
    const voxelGrid = this._voxelGrid;

    // Create opacity callback for transparency-aware AO
    const opacityCallback = this.considerTransparency ? this._createOpacityCallback() : null;

    // For each texel, calculate AO using voxel grid ray marching
    for (let ty = 0; ty < resY; ty++) {
      const v = (ty + 0.5) / resY;
      // Pre-compute V contribution for this row
      const vContribX = vScaleX * v;
      const vContribY = vScaleY * v;
      const vContribZ = vScaleZ * v;

      for (let tx = 0; tx < resX; tx++) {
        const u = (tx + 0.5) / resX;

        // Inline world position calculation (avoids function call overhead)
        const worldPos = {
          x: originX + uScaleX * u + vContribX,
          y: originY + uScaleY * u + vContribY,
          z: originZ + uScaleZ * u + vContribZ
        };

        // Sample AO - use transparency-aware version if enabled
        let ao;
        if (opacityCallback) {
          ao = voxelGrid.sampleAOWithTransparency(worldPos, rays, radius, intensity, pseudoCuboid, opacityCallback);
        } else {
          ao = voxelGrid.sampleAOWithRays(worldPos, rays, radius, intensity, pseudoCuboid);
        }
        aoData[ty * resX + tx] = ao;
      }
    }

    // Cache the raw data
    this._aoDataCache.set(cacheKey, aoData);

    // Generate and cache the texture (upscale 2x for smoother gradients)
    const texture = AOTextureGenerator.generateTextureUpscaled(
      aoData, resX, resY, 2
    );
    this._textureCache.set(cacheKey, texture);
  }

  /**
   * Get the world-space bounds of a face for AO texture generation.
   * Maps texel coordinates to world positions for occlusion sampling.
   * @param {Cuboid} cuboid - The cuboid
   * @param {string} face - Face name
   * @returns {Object} Face bounds with origin, uAxis, vAxis in world coordinates
   */
  static _getFaceWorldBounds(cuboid, face) {
    const { x, y, z, width, height, depth } = cuboid;

    switch (face) {
      case 'top':
        // Top face (Y+): origin at front-left, U goes +X, V goes -Z
        return {
          origin: { x, y: y + height, z: z + depth },
          uAxis: { x: width, y: 0, z: 0 },
          vAxis: { x: 0, y: 0, z: -depth }
        };
      case 'bottom':
        // Bottom face (Y-): origin at back-left, U goes +X, V goes +Z (toward front)
        // Opposite V direction from top because CSS scaleY(-1) flips the element,
        // so the texture coordinate system is also flipped
        return {
          origin: { x, y, z },
          uAxis: { x: width, y: 0, z: 0 },
          vAxis: { x: 0, y: 0, z: depth }
        };
      case 'front':
        // Front face (Z+, toward camera) - at z+depth plane
        // Origin at top-right, U goes -X, V goes -Y (mirrored)
        return {
          origin: { x: x + width, y: y + height, z: z + depth },
          uAxis: { x: -width, y: 0, z: 0 },
          vAxis: { x: 0, y: -height, z: 0 }
        };
      case 'back':
        // Back face (Z-, away from camera) - at z plane
        // Origin at top-left, U goes +X, V goes -Y (mirrored)
        return {
          origin: { x, y: y + height, z },
          uAxis: { x: width, y: 0, z: 0 },
          vAxis: { x: 0, y: -height, z: 0 }
        };
      case 'left':
        // Left face (X-): origin at top-front, U goes -Z, V goes -Y
        return {
          origin: { x, y: y + height, z: z + depth },
          uAxis: { x: 0, y: 0, z: -depth },
          vAxis: { x: 0, y: -height, z: 0 }
        };
      case 'right':
        // Right face (X+): origin at top-back, U goes +Z, V goes -Y
        return {
          origin: { x: x + width, y: y + height, z },
          uAxis: { x: 0, y: 0, z: depth },
          vAxis: { x: 0, y: -height, z: 0 }
        };
      default:
        return {
          origin: { x, y, z },
          uAxis: { x: 1, y: 0, z: 0 },
          vAxis: { x: 0, y: -1, z: 0 }
        };
    }
  }

  /**
   * Convert texel UV coordinates to world position.
   * @param {Object} bounds - Face bounds from _getFaceWorldBounds
   * @param {number} u - U coordinate (0-1)
   * @param {number} v - V coordinate (0-1)
   * @param {string} face - Face name (for offset from surface)
   * @returns {Object} World position {x, y, z}
   */
  static _texelToWorld(bounds, u, v, face) {
    const normal = Cuboid.FACE_NORMALS[face];
    const offset = 0.05; // Small offset from surface to start rays outside the surface

    return {
      x: bounds.origin.x + bounds.uAxis.x * u + bounds.vAxis.x * v + normal.x * offset,
      y: bounds.origin.y + bounds.uAxis.y * u + bounds.vAxis.y * v + normal.y * offset,
      z: bounds.origin.z + bounds.uAxis.z * u + bounds.vAxis.z * v + normal.z * offset
    };
  }

  // --- Incremental Update Methods ---

  /**
   * Queue a face for AO update. Used for non-blocking updates.
   * @param {Cuboid} cuboid - The cuboid needing update
   * @param {string} face - Face name
   * @param {number} priority - Higher = processed sooner (e.g., distance-based)
   */
  static queueUpdate(cuboid, face, priority = 0) {
    const cacheKey = `${cuboid.getKey()}|${face}`;

    // Don't queue if already pending
    if (this._pendingSet.has(cacheKey)) return;

    this._pendingUpdates.push({ cuboid, face, cacheKey, priority });
    this._pendingSet.add(cacheKey);
    this._pendingSortDirty = true;
  }

  /**
   * Process queued AO updates within a time budget.
   * Call this once per frame in the game loop.
   * @returns {number} Number of faces processed this frame
   */
  static processUpdateQueue() {
    const pending = this._pendingUpdates;
    if (this._pendingIndex >= pending.length) {
      if (pending.length > 0) {
        pending.length = 0;
        this._pendingIndex = 0;
      }
      if (this._levelSettling) this._levelSettling = false;
      return 0;
    }

    // Use burst budget during level settling or when queue is large
    const remaining = pending.length - this._pendingIndex;
    const budget = (this._levelSettling || remaining > this._burstThreshold)
      ? this._burstBudgetMs
      : this._normalBudgetMs;

    // Re-prioritize remaining queue when player has moved significantly
    // This ensures nearby cuboids always process first even as player explores
    if (this._playerPos && remaining > 10) {
      const pp = this._playerPos;
      const lastPP = this._lastPriorityPos;
      if (!lastPP ||
          Math.abs(pp.x - lastPP.x) > 2 || Math.abs(pp.y - lastPP.y) > 2 || Math.abs(pp.z - lastPP.z) > 2) {
        this._lastPriorityPos = { x: pp.x, y: pp.y, z: pp.z };
        // Update priorities for remaining items in-place
        for (let i = this._pendingIndex; i < pending.length; i++) {
          const item = pending[i];
          const c = item.cuboid;
          const dx = (c.x + (c.width || 0) / 2) - pp.x;
          const dy = (c.y + (c.height || 0) / 2) - pp.y;
          const dz = (c.z + (c.depth || 0) / 2) - pp.z;
          item.priority = Math.max(0, 100 - Math.sqrt(dx * dx + dy * dy + dz * dz));
        }
        this._pendingSortDirty = true;
      }
    }

    // Sort by priority only when dirty
    if (this._pendingSortDirty && pending.length > 1) {
      // Only sort the remaining portion (avoid re-sorting already processed items)
      const sub = pending.slice(this._pendingIndex);
      sub.sort((a, b) => b.priority - a.priority);
      for (let i = 0; i < sub.length; i++) {
        pending[this._pendingIndex + i] = sub[i];
      }
      this._pendingSortDirty = false;
    }

    const startTime = performance.now();
    let processed = 0;

    while (this._pendingIndex < pending.length) {
      // Guarantee at least 2 faces per frame, then respect budget
      if (processed >= 2 && performance.now() - startTime > budget) {
        break;
      }

      const { cuboid, face, cacheKey } = pending[this._pendingIndex];
      this._pendingIndex++;
      this._pendingSet.delete(cacheKey);

      // Skip if no longer dirty (may have been updated by parent extraction)
      if (!this._dirtyFaces.has(cacheKey)) continue;

      // Skip if cuboid no longer exists
      if (!this._world.cuboids.has(cuboid.getKey()) && !cuboid.isFragment) continue;

      // Calculate AO for this face
      this._calculateFaceAO(cuboid, face, cacheKey);
      this._dirtyFaces.delete(cacheKey);
      processed++;

      // Mark cuboid for re-render so update() picks up cached AO
      if (cuboid.faceElements) {
        cuboid.needsUpdate = true;
      }
    }

    // Reset when fully consumed
    if (this._pendingIndex >= pending.length) {
      pending.length = 0;
      this._pendingIndex = 0;
      if (this._levelSettling) {
        this._levelSettling = false;
      }
    }

    // Force a render pass so cuboid.update() picks up cached AO textures
    // (including fragment AO via _applyFragmentMaterials)
    if (processed > 0 && this._world) {
      this._world._forceRender = true;
      // Resample impostor AO as new floor AO data becomes available
      this._world._impostorAODirty = true;
    }

    return processed;
  }

  /**
   * Get the number of pending AO updates.
   * @returns {number} Number of pending updates
   */
  static getPendingCount() {
    return this._pendingUpdates.length - this._pendingIndex;
  }

  /**
   * Clear the update queue (e.g., when disabling AO).
   */
  static clearUpdateQueue() {
    this._pendingUpdates = [];
    this._pendingIndex = 0;
    this._pendingSet.clear();
  }

  /**
   * Set the per-frame time budget for AO updates.
   * @param {number} ms - Milliseconds per frame (1-16 recommended)
   */
  static setUpdateBudget(ms) {
    this._updateBudgetMs = Math.max(1, Math.min(16, ms));
  }

  // --- Invalidation Methods ---

  /**
   * Invalidate all AO caches. Called when settings change.
   */
  static invalidateAll() {
    this._aoDataCache.clear();
    this._textureCache.clear();
    this._dirtyFaces.clear();
    // Also clear update queue since all cached data is now invalid
    this._pendingUpdates = [];
    this._pendingIndex = 0;
    this._pendingSet.clear();
  }

  /**
   * Invalidate AO for a specific cuboid (all faces).
   * @param {string} cuboidKey - The cuboid's key
   */
  static invalidateCuboid(cuboidKey) {
    const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    for (const face of faces) {
      const cacheKey = `${cuboidKey}|${face}`;
      this._dirtyFaces.add(cacheKey);
    }
  }

  /**
   * Invalidate AO for all cuboids near a changed cuboid.
   * Uses spatial index for O(1) neighbor queries instead of O(n) iteration.
   * @param {Cuboid} changedCuboid - The cuboid that was added/removed/moved
   */
  static invalidateForCuboid(changedCuboid) {
    if (!this._world) return;

    // Skip invalidation in dungeon precalc mode (AO was calculated with full geometry)
    if (this._dungeonPrecalcMode) return;

    // Calculate the bounding box that needs invalidation
    // Any cuboid whose faces could "see" the changed cuboid needs updating
    const margin = this.radius + 1; // Extra margin for safety
    const minX = changedCuboid.x - margin;
    const maxX = changedCuboid.x + changedCuboid.width + margin;
    const minY = changedCuboid.y - margin;
    const maxY = changedCuboid.y + changedCuboid.height + margin;
    const minZ = changedCuboid.z - margin;
    const maxZ = changedCuboid.z + changedCuboid.depth + margin;

    // Track parent keys to invalidate (for fragments whose parent is no longer in world)
    const parentKeysToInvalidate = new Set();

    // Use spatial index for O(1) query if available, otherwise fall back to O(n)
    let nearbyCuboids;
    if (this._spatialIndex) {
      nearbyCuboids = this._spatialIndex.queryAABB(minX, minY, minZ, maxX, maxY, maxZ);
    } else {
      // Fallback to iterating all cuboids (for backwards compatibility)
      nearbyCuboids = new Set();
      for (const [key, cuboid] of this._world.cuboids) {
        if (cuboid.x + cuboid.width >= minX && cuboid.x <= maxX &&
            cuboid.y + cuboid.height >= minY && cuboid.y <= maxY &&
            cuboid.z + cuboid.depth >= minZ && cuboid.z <= maxZ) {
          nearbyCuboids.add(cuboid);
        }
      }
    }

    // Invalidate all nearby cuboids
    for (const cuboid of nearbyCuboids) {
      const key = cuboid.getKey();
      this.invalidateCuboid(key);

      // If this is a fragment, also invalidate its parent's cached data
      // (parent may have been removed but still have cached AO data)
      if (cuboid.isFragment && cuboid.parentKey) {
        parentKeysToInvalidate.add(cuboid.parentKey);
      }
    }

    // Invalidate parent keys that are no longer in the world
    for (const parentKey of parentKeysToInvalidate) {
      if (!this._world.cuboids.has(parentKey)) {
        this.invalidateCuboid(parentKey);
      }
    }
  }

  /**
   * Invalidate AO for all cuboids within a radius of a point.
   * Used when a cuboid is added/removed to update neighbors.
   * @param {number} x - Center X
   * @param {number} y - Center Y
   * @param {number} z - Center Z
   * @param {number} radius - Invalidation radius
   */
  static invalidateArea(x, y, z, radius) {
    if (!this._world) return;

    // Invalidate all cuboids that might be affected
    const searchRadius = radius + this.radius; // Account for AO sampling radius
    for (const [key, cuboid] of this._world.cuboids) {
      // Check if any part of the cuboid is within range
      const closestX = Math.max(cuboid.x, Math.min(x, cuboid.x + cuboid.width));
      const closestY = Math.max(cuboid.y, Math.min(y, cuboid.y + cuboid.height));
      const closestZ = Math.max(cuboid.z, Math.min(z, cuboid.z + cuboid.depth));

      const dist = Math.sqrt(
        (closestX - x) ** 2 + (closestY - y) ** 2 + (closestZ - z) ** 2
      );

      if (dist <= searchRadius) {
        this.invalidateCuboid(key);
      }
    }
  }

  // --- Dungeon Mode Pre-calculation ---

  /** Whether dungeon precalculation is active (skip per-cuboid invalidation) */
  static _dungeonPrecalcMode = false;
  /** Cached cuboid defs for re-precalculation on config change */
  static _dungeonDefs = null;

  /**
   * Build the voxel grid from cuboid definition objects (not Cuboid instances).
   * Used by dungeon mode to build a COMPLETE grid including not-yet-loaded geometry,
   * so AO ray marching sees all walls and produces accurate shadows.
   * @param {Array<Object>} defs - Array of {x, y, z, width, height, depth} objects
   * @param {World} world - World reference for cache storage
   */
  static buildVoxelGridFromDefs(defs, world) {
    this._world = world;
    this._voxelGrid = new AOVoxelGrid(1);
    this._spatialIndex = new AOSpatialIndex(Math.max(2, Math.ceil(this.radius + 1)));

    // Build grid from all def objects (they have x/y/z/width/height/depth)
    for (const def of defs) {
      this._voxelGrid.addCuboid(def);
    }

    // Build spatial index from currently loaded cuboids only
    // (spatial index is used for invalidation queries, which we skip in dungeon mode)
    this._spatialIndex.rebuild(world.cuboids);

    this.invalidateAll();
    this._dungeonPrecalcMode = true;
    this._dungeonDefs = defs;
  }

  /**
   * Pre-calculate AO for dungeon cuboid definitions.
   * Calculates floor tops synchronously (most visible, loaded permanently).
   * Queues wall faces for non-blocking processing.
   * @param {Array<Object>} defs - All cuboid defs (permanent + streamed)
   */
  static precalculateForDefs(defs) {
    if (!this.enabled || !this._voxelGrid) return;

    const faces = ['top', 'front', 'back', 'left', 'right'];

    for (const def of defs) {
      // Create a pseudo-cuboid with getKey() for cache keying
      const pseudo = {
        x: def.x, y: def.y, z: def.z,
        width: def.width, height: def.height, depth: def.depth,
        getKey() { return `${this.x},${this.y},${this.z},${this.width},${this.height},${this.depth}`; }
      };

      // Determine which faces to pre-calculate based on role
      let facesToCalc;
      if (def.role === 'floor') {
        facesToCalc = ['top']; // Floor tops are most visible
      } else {
        facesToCalc = faces; // Walls, pillars, chests: all visible faces
      }

      for (const face of facesToCalc) {
        // Swap front/back for correct AO mapping (matches getTextureForFace)
        let samplingFace = face;
        if (face === 'front') samplingFace = 'back';
        else if (face === 'back') samplingFace = 'front';

        const cacheKey = `${pseudo.getKey()}|${samplingFace}`;

        // Queue for non-blocking processing
        if (!this._pendingSet.has(cacheKey)) {
          // Floor tops get high priority; closer cuboids get higher priority
          let priority = def.role === 'floor' ? 10 : 1;
          if (this._playerPos) {
            const dx = (def.x + def.width / 2) - this._playerPos.x;
            const dy = (def.y + def.height / 2) - this._playerPos.y;
            const dz = (def.z + def.depth / 2) - this._playerPos.z;
            const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            priority += Math.max(0, 100 - dist);
          }
          this._pendingUpdates.push({ cuboid: pseudo, face: samplingFace, cacheKey, priority });
          this._pendingSet.add(cacheKey);
          this._dirtyFaces.add(cacheKey);
        }
      }
    }

    this._pendingSortDirty = true;
    // Use aggressive budget during dungeon precalc — player sees loading screen
    this._burstBudgetMs = 14;
    this._levelSettling = true;
  }

  /**
   * Exit dungeon pre-calculation mode. Restores normal per-cuboid invalidation.
   */
  static exitDungeonMode() {
    this._dungeonPrecalcMode = false;
    this._dungeonDefs = null;
    this._burstBudgetMs = 10; // Reset to normal burst budget
  }

  // --- Voxel Grid Management ---

  /**
   * Add a cuboid to the voxel grid and spatial index.
   * @param {Cuboid} cuboid - The cuboid to add
   */
  static addToIndex(cuboid) {
    if (this._voxelGrid) {
      this._voxelGrid.addCuboid(cuboid);
    }
    if (this._spatialIndex) {
      this._spatialIndex.insert(cuboid);
    }
  }

  /**
   * Remove a cuboid from the voxel grid and spatial index.
   * Note: For overlapping cuboids, rebuild is safer.
   * @param {Cuboid} cuboid - The cuboid to remove
   */
  static removeFromIndex(cuboid) {
    if (this._voxelGrid) {
      this._voxelGrid.removeCuboid(cuboid);
    }
    if (this._spatialIndex) {
      this._spatialIndex.remove(cuboid.getKey());
    }
  }

  /**
   * Rebuild the voxel grid and spatial index from scratch.
   * @param {Map<string, Cuboid>} cuboids - Map of all cuboids
   */
  static rebuildIndex(cuboids) {
    if (this._voxelGrid) {
      this._voxelGrid.rebuild(cuboids);
    }
    if (this._spatialIndex) {
      this._spatialIndex.rebuild(cuboids);
    }
  }

  // --- Utility Methods ---

  /**
   * Enable or disable AO.
   * @param {boolean} enabled - Whether AO should be enabled
   */
  static setEnabled(enabled) {
    if (this.enabled === enabled) return;

    this.enabled = enabled;

    if (!enabled) {
      // When disabling, exit dungeon mode
      this._dungeonPrecalcMode = false;
      this._dungeonDefs = null;
    }
    // Note: When enabling, the caller (main.js AO toggle handler) is responsible
    // for choosing between initialize() (normal) and buildVoxelGridFromDefs() (dungeon).
    // This avoids duplicating the dungeon-mode detection logic here.
  }

  /**
   * Set AO intensity.
   * @param {number} intensity - Intensity value 0-2
   */
  static setIntensity(intensity) {
    this.intensity = Math.max(0, Math.min(2, intensity));
  }

  /**
   * Set AO ray march radius.
   * @param {number} radius - Radius in world units
   */
  static setRadius(radius) {
    this.radius = Math.max(0.5, radius);
  }

  /**
   * Set number of rays per sample.
   * @param {number} numRays - Number of rays (8-32 recommended)
   */
  static setNumRays(numRays) {
    this.numRays = Math.max(4, Math.min(64, numRays));
  }

  /**
   * Set AO texture resolution (texels per world unit).
   * @param {number} texelsPerUnit - Resolution (2-8 recommended)
   */
  static setTexelsPerUnit(texelsPerUnit) {
    this.texelsPerUnit = Math.max(1, Math.min(16, texelsPerUnit));
  }

  /**
   * Set whether AO considers face transparency.
   * When enabled, semi-transparent faces cast partial shadows.
   * @param {boolean} consider - Whether to consider transparency
   */
  static setConsiderTransparency(consider) {
    this.considerTransparency = !!consider;
  }

  /**
   * Sample AO value at a world position on a specific cuboid's face.
   * Efficiently reuses cached AO data - no ray marching needed.
   * @param {Cuboid} cuboid - The cuboid whose face to sample
   * @param {string} face - Face name (typically 'top')
   * @param {number} worldX - World X position
   * @param {number} worldZ - World Z position
   * @returns {number} AO value (0-1, where 1 = fully lit, lower = more occluded)
   */
  static sampleAOAtWorldPoint(cuboid, face, worldX, worldZ) {
    if (!this.enabled) return 1;

    // Get cached AO data for this face
    const cacheKey = `${cuboid.getKey()}|${face}`;
    const aoData = this._aoDataCache.get(cacheKey);

    if (!aoData) {
      // No cached data - return neutral (1 = fully lit)
      return 1;
    }

    // Get face dimensions and resolution
    const faceDims = this._getFaceDimensions(cuboid, face);
    const resX = Math.max(2, Math.min(64, Math.ceil(faceDims.width * this.texelsPerUnit)));
    const resY = Math.max(2, Math.min(64, Math.ceil(faceDims.height * this.texelsPerUnit)));

    // Convert world position to UV coordinates on the face
    let u, v;
    switch (face) {
      case 'top':
        // Top face: U = X axis (left to right), V = Z axis (front to back)
        u = (worldX - cuboid.x) / cuboid.width;
        v = (cuboid.z + cuboid.depth - worldZ) / cuboid.depth;
        break;
      case 'bottom':
        u = (worldX - cuboid.x) / cuboid.width;
        v = (worldZ - cuboid.z) / cuboid.depth;
        break;
      default:
        // For side faces, just return 1 (not commonly needed)
        return 1;
    }

    // Clamp UV to valid range
    u = Math.max(0, Math.min(1, u));
    v = Math.max(0, Math.min(1, v));

    // Convert UV to texel coordinates
    const px = u * resX - 0.5;
    const py = v * resY - 0.5;

    // Bilinear sample
    return this._bilinearSample(aoData, resX, resY, px, py);
  }

  /**
   * Get statistics about the AO system.
   * @returns {Object} Statistics
   */
  static getStats() {
    return {
      enabled: this.enabled,
      intensity: this.intensity,
      radius: this.radius,
      numRays: this.numRays,
      texelsPerUnit: this.texelsPerUnit,
      considerTransparency: this.considerTransparency,
      cachedTextures: this._textureCache.size,
      cachedAoData: this._aoDataCache.size,
      dirtyFaces: this._dirtyFaces.size,
      pendingUpdates: this._pendingUpdates.length,
      updateBudgetMs: this._updateBudgetMs,
      voxelGrid: this._voxelGrid?.getStats() || null,
      spatialIndex: this._spatialIndex?.getStats() || null
    };
  }
}
